import { Injectable } from '@angular/core';
import { EntityState, EntityStore, StoreConfig } from '@datorama/akita';
import { TranslateService } from '@ngx-translate/core';
import { first } from 'lodash-es';
import unset from 'lodash-es/unset';
import { Observable, defer, of } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';

import { Module } from 'module/module';
import { ModuleQuestionField } from 'module/page/question/field/module-question-field';
import { ModuleQuestionFieldI18n } from 'module/page/question/field/module-question-field-i18n';
import { ModuleQuestionFieldType } from 'module/page/question/model/field/module-question-field.type';
import { ModuleQuestionType } from 'module/page/question/model/module-question.type';
import { ModuleQuestion } from 'module/page/question/module-question';
import { ModuleQuestionI18n } from 'module/page/question/module-question-i18n';
import { ModuleQuestionRepositoryPort } from 'module/page/question/port/module-question-repository.port';

import { AppLocator } from '../../../../app.locator';

export interface ModuleQuestionFieldI18nDocument {
	readonly label: string;
	readonly locale: string;
	readonly placeholder: string;
}

export interface ModuleQuestionFieldDocument {
	readonly id: string;
	readonly name: string;
	readonly type: string;
	readonly value: string;
	readonly position: number;
	readonly minLines: number;
	readonly condition: string;
	readonly gridClass: string;
	readonly isRequired: boolean;
	readonly isExtraField: boolean;
	readonly optionsGroup: string;
	readonly optionsScript: string;
	readonly updateTimestamp: number;
	readonly optionsGridClass: string;
	readonly localizations: ModuleQuestionFieldI18nDocument[];
}

export interface ModuleQuestionI18nDocument {
	readonly locale: string;
	readonly question: string;
	readonly addSiblingLabel: string;
	readonly addDuplicateLabel: string;
}

export interface ModuleQuestionDocument {
	readonly id: string;
	readonly name: string;
	readonly position: number;
	readonly condition: string;
	readonly uniqFilter: string;
	readonly canDuplicate: boolean;
	readonly canAddSibling: boolean;
	readonly sourceModuleId: string;
	readonly updateTimestamp: number;
	readonly cssClass: string;
	readonly gridClass: string;
	readonly sourceQuestionId: string;
	readonly type: ModuleQuestionType;
	readonly fields: ModuleQuestionFieldDocument[];
	readonly localizations: ReadonlyArray<ModuleQuestionI18nDocument>;
}

export type ModuleQuestionState = EntityState<ModuleQuestionDocument, string>;

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'moduleQuestions' })
export class ModuleQuestionStore extends EntityStore<ModuleQuestionState> {
	constructor() {
		super();
	}

	public get _translateService(): TranslateService {
		return AppLocator.inject(TranslateService);
	}

	public asDocument(question: ModuleQuestion): ModuleQuestionDocument {
		const document: ModuleQuestionDocument = question
			? {
					id: question.id,
					name: question.name,
					type: question.type,
					cssClass: question.cssClass,
					position: question.position,
					condition: question.condition,
					gridClass: question.gridClass,
					uniqFilter: question.uniqFilter,
					canDuplicate: question.canDuplicate,
					canAddSibling: question.canAddSibling,
					localizations: question.localizations,
					sourceModuleId: question.sourceModuleId,
					updateTimestamp: question.updateTimestamp,
					sourceQuestionId: question.sourceQuestionId,
					fields: question.fields.map((field) => ({
						optionsGridClass: field.optionsGridClass,
						updateTimestamp: field.updateTimestamp,
						localizations: field.localizations,
						optionsScript: field.optionsScript,
						optionsGroup: field.optionsGroup,
						isExtraField: field.isExtraField,
						isRequired: field.isRequired,
						gridClass: field.gridClass,
						condition: field.condition,
						minLines: field.minLines,
						position: field.position,
						value: field.value,
						name: field.name,
						type: field.type,
						id: field.id,
					})),
			  }
			: null;

		if ((question.fields || []).length === 0) {
			unset(document, 'fields');
		}

		return document;
	}

	public asEntity(
		document: ModuleQuestionDocument,
		modules$: (ids: string[]) => Observable<Module[]>
	): ModuleQuestion {
		const localizations = !!document
			? document.localizations.map(
					(loc) =>
						new ModuleQuestionI18n(
							loc.locale,
							loc.question,
							loc.addSiblingLabel,
							loc.addDuplicateLabel
						)
			  )
			: [];

		const moduleQuestionRepositoryPort =
			AppLocator.inject<ModuleQuestionRepositoryPort>(
				ModuleQuestionRepositoryPort
			);

		return document
			? new ModuleQuestion(
					document.id,
					document.name,
					document.position,
					document.condition,
					document.uniqFilter,
					document.canDuplicate,
					document.canAddSibling,
					document.sourceModuleId,
					document.updateTimestamp,
					document.sourceQuestionId,
					ModuleQuestionType[document.type],
					(document.fields || []).map((field) =>
						this._asModuleQuestionFieldEntity(field)
					),
					localizations,
					document.cssClass,
					document.gridClass,
					this._observeLocalization(localizations),
					document.sourceQuestionId
						? moduleQuestionRepositoryPort.observeModuleQuestionById(
								document.sourceQuestionId
						  )
						: of(null),
					document.sourceModuleId
						? modules$([document.sourceModuleId]).pipe(map(first))
						: of(null)
			  )
			: null;
	}

	private _observeLocalization<T extends { locale: string }>(
		localizations: ReadonlyArray<T>
	): Observable<T> {
		return localizations && localizations.length > 0
			? defer(() =>
					this._translateService.onLangChange.pipe(
						map((e) => e.lang),
						startWith(this._translateService.currentLang)
					)
			  ).pipe(
					map((lang) => localizations.find((loc) => loc.locale === lang)),
					map((loc) => (!loc ? localizations[0] : loc)),
					shareReplay({ bufferSize: 1, refCount: true })
			  )
			: of(null);
	}

	private _asModuleQuestionFieldEntity(
		document: ModuleQuestionFieldDocument
	): ModuleQuestionField {
		const localizations = !!document
			? (document.localizations || []).map(
					(loc) =>
						new ModuleQuestionFieldI18n(loc.label, loc.locale, loc.placeholder)
			  )
			: [];

		return document
			? new ModuleQuestionField(
					document.id,
					document.name,
					document.value,
					document.position,
					document.minLines,
					document.condition,
					document.gridClass,
					document.isRequired,
					document.isExtraField,
					document.optionsGroup,
					document.optionsScript,
					document.updateTimestamp,
					document.optionsGridClass,
					ModuleQuestionFieldType[document.type],
					localizations,
					this._observeLocalization(localizations)
			  )
			: null;
	}
}
